import React, { Component } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { localize } from 'react-localize-redux';

import Dialog from '@material-ui/core/Dialog';

import { SyncLoader } from 'halogenium';

import { AccentColor } from 'helpers/AccentColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { onlyNumbers } from 'helpers/Formatter';

import { showFailure } from 'actions/notificationActions';
import { makePayment, authorizePayment } from 'actions/tenantProfileActions';
import { getTenantTodoList } from 'actions/todoActions';

import { createToken } from './BamboraFields';
import { BamboraPaymentForm } from './BamboraPaymentForm';
import { BamboraPaymentConfirmation } from './BamboraPaymentConfirmation';
import { BamboraSuccessfulPayment } from './BamboraSuccessfulPayment';
import { BamboraFailure } from './BamboraFailure';

const StyledDialogContainer = styled.div`
  padding: 0.5rem 2rem;
`;

const StyledLoadingContainer = styled.div`
  width: 100%;
  text-align: center;
  padding: 3rem 0;
`;

const StyledLoadingP = styled.p`
  margin-top: 2rem;
`;

const LoadingMessage = ({ isLoading, message }) => {
  if (!isLoading) return null;

  return (
    <StyledLoadingContainer>
      <SyncLoader color={AccentColor()} size="16px" margin="auto" />
      <StyledLoadingP>{message}</StyledLoadingP>
    </StyledLoadingContainer>
  );
};

class BamboraTenantPayment extends Component {
  state = {
    confirmationDialogOpen: false,
    paymentAuthorizing: false,
    sendingPayment: false,
    paymentDetails: {},
    paymentSuccessOpen: false,
    paymentFailureOpen: false,
  };

  finalizePayment = async ev => {
    this.setState({ sendingPayment: true });

    this.toggleConfirmationDialog();
    const { paymentDetails } = this.state;

    try {
      const bamboraResponse = await createToken();
      if (!bamboraResponse.ok) {
        throw new Error('BPMS failed');
      }
      // process token using our Payments API
      const promise = this.props.makePayment(bamboraResponse.data.token, {
        ...paymentDetails,
        payment_with_fee: this.props.paymentWithFee,
      });

      promise.then(bpmsResponse => {
        if (!bpmsResponse.value) {
          this.showPaymentFailure();
          return;
        }
        this.showPaymentSuccess();
      });
    } catch (error) {
      this.showPaymentFailure();
    }
  };

  handlePaymentFormSubmit = async values => {
    this.setState({ paymentAuthorizing: true });

    const paymentDetails = {
      payment_amount: parseFloat(onlyNumbers(values.paymentAmount)).toFixed(2),
      card_holder_name: values.cardHolderName,
    };
    try {
      const bamboraResponse = await createToken();

      if (!bamboraResponse.ok) {
        throw new Error('Bambora request failed');
      }

      const bpmsResponse = await this.props.authorizePayment(
        bamboraResponse.data.token,
        paymentDetails
      );

      if (!bpmsResponse.value) {
        this.showPaymentFailure();
      } else {
        this.toggleConfirmationDialog();
      }
      this.setState({ paymentAuthorizing: false, paymentDetails });
    } catch (error) {
      this.showPaymentFailure();
    }
  };

  showPaymentFailure = () => {
    this.props.showFailure(this.props.translate('errorPaymentGateway').props.children);
    this.setState({
      paymentFailureOpen: true,
      paymentSuccessOpen: false,
      sendingPayment: false,
      paymentAuthorizing: false,
    });
  };

  showPaymentSuccess = () => {
    this.setState({
      paymentFailureOpen: false,
      paymentSuccessOpen: true,
      sendingPayment: false,
    });
  };

  closePaymentSuccess = () => {
    this.props.getTenantTodoList();
    this.props.toggleOpen();
    this.props.toggleParent();
    this.setState({
      confirmationDialogOpen: false,
      paymentAuthorizing: false,
      sendingPayment: false,
      paymentDetails: {},
      paymentSuccessOpen: false,
      paymentFailureOpen: false,
    });
  };

  closePaymentFailure = () => {
    this.setState({
      confirmationDialogOpen: false,
      paymentAuthorizing: false,
      sendingPayment: false,
      paymentSuccessOpen: false,
      paymentFailureOpen: false,
    });
  };

  handleClosePaymentForm = () => {
    this.props.toggleOpen();
  };

  toggleConfirmationDialog = () => {
    this.setState({
      confirmationDialogOpen: !this.state.confirmationDialogOpen,
    });
  };

  render() {
    const isLoading = this.state.paymentAuthorizing || this.state.sendingPayment;
    const showPaymentSuccess = this.state.paymentSuccessOpen;
    const showPaymentFailure = this.state.paymentFailureOpen;
    const showPaymentForm = !(
      isLoading ||
      showPaymentSuccess ||
      showPaymentFailure ||
      (this.state.confirmationDialogOpen && this.props.paymentWithFee)
    );
    const showPaymentConfirmation =
      !(isLoading || showPaymentSuccess || showPaymentFailure) &&
      this.state.confirmationDialogOpen &&
      this.props.paymentWithFee;

    return (
      <React.Fragment>
        <Dialog
          fullScreen={this.props.fullScreen}
          open={this.props.open}
          aria-labelledby="payment-dialog"
          classes={{ paper: 'form-dialog' }}
        >
          <StyledDialogContainer>
            <LoadingMessage
              isLoading={isLoading}
              message={
                this.state.sendingPayment
                  ? getTranslatedString('bamboraCheckout.processingPleaseWait')
                  : getTranslatedString('bpms.retrievingPaymentFeePleaseWait')
              }
            />
            <BamboraPaymentForm
              isHidden={!showPaymentForm}
              outstandingBalance={this.props.outstandingBalance}
              handleSubmit={this.handlePaymentFormSubmit}
              handleClose={this.handleClosePaymentForm}
              paymentAuthorizing={this.state.paymentAuthorizing}
            />
            {showPaymentConfirmation && (
              <BamboraPaymentConfirmation
                paymentWithFee={this.props.paymentWithFee}
                sendingPayment={this.state.sendingPayment}
                toggleConfirmationDialog={this.toggleConfirmationDialog}
                finalizePayment={this.finalizePayment}
              />
            )}
            {showPaymentSuccess && (
              <BamboraSuccessfulPayment
                handleClose={this.closePaymentSuccess}
                paymentSuccessDetails={this.props.paymentSuccessDetails}
              />
            )}
            {showPaymentFailure && (
              <BamboraFailure
                paymentError={this.props.paymentError}
                paymentAuthorizeError={this.props.paymentAuthorizeError}
                handleClose={this.closePaymentFailure}
              />
            )}
          </StyledDialogContainer>
        </Dialog>
      </React.Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { paymentDetails } = state.tenantProfile;
  return {
    paymentWithFee: paymentDetails.paymentWithFee,
    paymentSuccessDetails: paymentDetails.paymentSuccessDetails,
    paymentAuthorizeError: paymentDetails.paymentAuthorizeError,
    paymentError: paymentDetails.paymentError,
  };
};

BamboraTenantPayment = connect(mapStateToProps, {
  authorizePayment,
  makePayment,
  showFailure,
  getTenantTodoList,
})(BamboraTenantPayment);

export default localize()(BamboraTenantPayment);
